/**
 * AssignmentPicker — J-A20 modal-table for batch-assigning Tests to a Company.
 *
 * Composed from `@/components/ui` primitives only (Inv 15). The picker is a
 * modal Dialog containing a filterable table; the parent controls open/close
 * and supplies the loaded catalogue.
 */

import { useMemo, useState, type ReactElement } from 'react';
import { Badge, Button, Card, Heading, Input } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';
import type {
  CompanyAssignmentItem,
  CompanyAssignmentsPayload,
} from '../../state/admin/api';

export interface AssignmentPickerProps {
  open: boolean;
  payload: CompanyAssignmentsPayload | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (changes: { assign: string[]; unassign: string[] }) => void;
}

const TRACKS = ['architecture', 'mep', 'structural', 'interior'] as const;
const LEVELS = ['novice', 'intermediate', 'advanced', 'bim_champion'] as const;
const STATUSES = ['active', 'draft', 'archived'] as const;

type TrackFilter = '' | (typeof TRACKS)[number];
type LevelFilter = '' | (typeof LEVELS)[number];
type StatusFilter = '' | (typeof STATUSES)[number];

function statusVariant(s: CompanyAssignmentItem['status']): BadgeVariant {
  switch (s) {
    case 'active':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
      return 'neutral';
  }
}

export function AssignmentPicker(props: AssignmentPickerProps): ReactElement | null {
  const { open, payload, loading, saving, error, onClose, onSave } = props;

  // Pending state — flipped checkbox state per Test ID, only differences
  // from the initial loaded state are saved.
  const [pendingAssign, setPendingAssign] = useState<Set<string>>(new Set());
  const [pendingUnassign, setPendingUnassign] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [trackFilter, setTrackFilter] = useState<TrackFilter>('');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

  // Reset pending state every time the payload identity changes (re-open).
  const payloadKey = payload?.company.id ?? '';
  const [lastPayloadKey, setLastPayloadKey] = useState('');
  if (payloadKey !== lastPayloadKey) {
    setLastPayloadKey(payloadKey);
    setPendingAssign(new Set());
    setPendingUnassign(new Set());
  }

  const filtered = useMemo(() => {
    if (!payload) return [];
    const needle = searchTerm.trim().toLowerCase();
    return payload.items.filter((t) => {
      if (statusFilter !== '' && t.status !== statusFilter) return false;
      if (trackFilter !== '' && t.track !== trackFilter) return false;
      if (levelFilter !== '' && t.level !== levelFilter) return false;
      if (needle !== '' && !t.title.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [payload, searchTerm, trackFilter, levelFilter, statusFilter]);

  if (!open) return null;

  const handleToggle = (item: CompanyAssignmentItem): void => {
    const id = item.id;
    if (item.assigned) {
      // Currently assigned → toggling means staging an unassign.
      setPendingUnassign((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setPendingAssign((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  const handleSave = (): void => {
    onSave({
      assign: Array.from(pendingAssign),
      unassign: Array.from(pendingUnassign),
    });
  };

  const pendingCount = pendingAssign.size + pendingUnassign.size;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="assignment-picker-heading"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <Card variant="default" className="mx-auto flex max-h-screen w-full max-w-4xl flex-col p-0">
        <header className="border-b border-line px-6 py-4">
          <Heading
            level="h2"
            id="assignment-picker-heading"
            className="text-h3 text-ink"
          >
            Assign Tests — {payload?.company.name ?? 'Company'}
          </Heading>
          <p className="mt-1 font-sans text-small text-muted">
            Toggle Tests to grant or revoke catalogue access. Changes apply on Save.
          </p>
        </header>

        {/* Filter band */}
        <div className="border-b border-line bg-bg-warm px-6 py-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Input
              type="text"
              placeholder="Search tests…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              aria-label="Search tests"
            />
            <select
              value={trackFilter}
              onChange={(e) => {
                setTrackFilter(e.target.value as TrackFilter);
              }}
              aria-label="Filter by track"
              className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
            >
              <option value="">All tracks</option>
              {TRACKS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value as LevelFilter);
              }}
              aria-label="Filter by level"
              className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
            >
              <option value="">All levels</option>
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
              }}
              aria-label="Filter by status"
              className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="px-6 py-8 text-center font-sans text-body text-muted">
              Loading catalogue…
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-6 py-8 text-center font-sans text-body text-muted">
              No tests match the current filters.
            </p>
          ) : (
            <table className="w-full border-collapse text-small" aria-label="Test catalogue">
              <thead>
                <tr className="bg-bg-warm">
                  <th
                    scope="col"
                    className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Assign
                  </th>
                  <th
                    scope="col"
                    className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Test
                  </th>
                  <th
                    scope="col"
                    className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Track / Level
                  </th>
                  <th
                    scope="col"
                    className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const willBeAssigned = t.assigned
                    ? !pendingUnassign.has(t.id)
                    : pendingAssign.has(t.id);
                  return (
                    <tr key={t.id} className="border-b border-line last:border-b-0">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={willBeAssigned}
                          onChange={() => {
                            handleToggle(t);
                          }}
                          aria-label={`Assign ${t.title}`}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="px-4 py-2 font-sans text-body text-ink">
                        {t.title}
                      </td>
                      <td className="px-4 py-2 font-mono text-mono text-muted">
                        {(t.track ?? '—') + ' / ' + (t.level ?? '—')}
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-line px-6 py-3">
          {error !== null ? (
            <p role="alert" className="mb-3 font-sans text-small text-error">
              {error}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-3">
            <p className="font-sans text-small text-muted">
              {pendingCount === 0
                ? 'No pending changes'
                : `${String(pendingCount)} change${pendingCount === 1 ? '' : 's'} pending`}
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSave}
                disabled={saving || pendingCount === 0}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </footer>
      </Card>
    </div>
  );
}
